import React, { useState, useRef, useEffect } from "react";
import { jwtDecode } from "jwt-decode";
import { Info } from "lucide-react";
import { useAuth } from "./authentication/AuthContext";

const SearchBar = ({
  selectedCategory,
  searchQuery,
  setSearchQuery,
  setSelectedCategory,
  orderInfo,
  onSearchKeyDown
}) => {
  const [showShortcutModal, setShowShortcutModal] = useState(false);
  const [isSearchBarOpen, setIsSearchBarOpen] = useState(false);
  const [shouldClearSearch, setShouldClearSearch] = useState(false);
  const searchBarRef = useRef(null);

  const { user } = useAuth();
  const userId = user?.id || "";
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);
  const debounceTimerRef = useRef(null);
  const lastPushedQueryRef = useRef(searchQuery || "");
 
  useEffect(() => {
    if (searchQuery !== lastPushedQueryRef.current) {
      setLocalSearchQuery(searchQuery);
      lastPushedQueryRef.current = searchQuery || "";
    }
  }, [searchQuery]);
 
  const toggleSearchBar = () => {
    setIsSearchBarOpen(!isSearchBarOpen);
  };
 
 
  const handleClickOutside = (event) => {
    if (searchBarRef.current && !searchBarRef.current.contains(event.target)) {
      setIsSearchBarOpen(false);
    }
  };
 
  useEffect(() => {
    if (isSearchBarOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
 
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isSearchBarOpen]);
 
 
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setLocalSearchQuery(query);
    setShouldClearSearch(true);
 
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    debounceTimerRef.current = setTimeout(() => {
      lastPushedQueryRef.current = query;
      setSearchQuery(query);
    }, 250);
  };
 
  const handleClearSearch = () => {
    setLocalSearchQuery("");
    lastPushedQueryRef.current = "";
    setSearchQuery("");
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
  };

  return (
    <div
      ref={searchBarRef}
      className="flex relative flex-row justify-between items-center bg-white px-5 py-4 mb-4 rounded-2xl border border-slate-100 shadow-sm"
    >
      {/* <p
        className={`text-lg font-bold ${isSearchBarOpen ? "hidden" : "block"}`}
      >
        Search Items
      </p> */}

<p
  className={`text-base font-bold text-slate-800 ${
    isSearchBarOpen ? "hidden" : "block"
  }`}
>
  {orderInfo ? (
    orderInfo.orderType === "DIRECT" ? (
      <span className="flex items-center gap-1.5 bg-slate-50 px-3 py-1.5 rounded-xl border border-slate-100 text-sm">
        🛍 Parcel {orderInfo.parcelToken || ""}
      </span>
    ) : (
      <span className="flex items-center gap-1.5 bg-slate-50 px-3 py-1.5 rounded-xl border border-slate-100 text-sm">
        {orderInfo.sectionType === "TABLE" ? "🪑 T" : "🏨 R"}
        {orderInfo.unitNumber}

        {/* Room Type Badge */}
        {orderInfo.sectionType === "ROOM" &&
          orderInfo.roomType &&
          orderInfo.roomType !== "NA" && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-slate-200/80 text-gray-700">
              {orderInfo.roomType.toUpperCase().includes("AC") &&
              !orderInfo.roomType.toUpperCase().includes("NON")
                ? "❄️ AC"
                : "🌤 Non-AC"}
            </span>
          )}

        <span className="text-xs text-gray-400 font-normal">
          ({orderInfo.floorName})
        </span>
      </span>
    )
  ) : (
    <span className="flex items-center gap-1.5 bg-rose-50 text-rose-700 px-3 py-1.5 rounded-xl border border-rose-100 text-sm">
      Select Order
    </span>
  )}
</p>




      <div className="flex items-center space-x-2">
        <div className="lg:hidden">
          <button
            onClick={toggleSearchBar}
            className={`text-2xl p-2 rounded-xl bg-slate-50 border border-slate-100 text-slate-600 ${isSearchBarOpen ? "hidden" : "block"}`}
          >
            <i className="fas fa-search text-base"></i>
          </button>
        </div>
        {isSearchBarOpen && (
          <div className="flex flex-row items-center space-x-2 lg:hidden w-full relative">
            <div className="relative w-full">
              <input
                id="pos-search-input-mobile"
                type="text"
                className="bg-slate-50 border border-slate-200 text-slate-800 placeholder-slate-400 rounded-xl px-4 py-2 pr-10 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition duration-150 focus:outline-none w-full text-sm"
                placeholder="Search..."
                value={localSearchQuery}
                onChange={handleSearchChange}
                onKeyDown={onSearchKeyDown}
              />
              {localSearchQuery && (
                <button
                  type="button"
                  onClick={handleClearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 focus:outline-none flex items-center justify-center p-1 rounded-full hover:bg-slate-200/50 transition-colors"
                  title="Clear search"
                >
                  <span className="text-sm font-bold leading-none">✕</span>
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={() => setShowShortcutModal(true)}
              className="p-2 text-slate-400 hover:text-teal-600 hover:bg-slate-50 border border-transparent hover:border-slate-100 rounded-xl transition duration-150"
              title="Keyboard Shortcuts Cheat Sheet"
            >
              <Info className="w-5 h-5" />
            </button>
          </div>
        )}
        <div className="hidden lg:flex flex-row items-center space-x-2">
          <div className="relative">
            <input
              id="pos-search-input"
              type="text"
              className="bg-slate-50 border border-slate-200 text-slate-800 placeholder-slate-400 rounded-xl px-4 py-2 pr-10 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition duration-150 focus:outline-none w-64 lg:w-72 text-sm"
              placeholder="Search... (Press '/' to focus)"
              value={localSearchQuery}
              onChange={handleSearchChange}
              onKeyDown={onSearchKeyDown}
            />
            {localSearchQuery && (
              <button
                type="button"
                onClick={handleClearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 focus:outline-none flex items-center justify-center p-1 rounded-full hover:bg-slate-200/50 transition-colors"
                title="Clear search"
              >
                <span className="text-sm font-bold leading-none">✕</span>
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={() => setShowShortcutModal(true)}
            className="p-2 text-slate-400 hover:text-teal-600 hover:bg-slate-50 border border-slate-100 rounded-xl transition duration-150"
            title="Keyboard Shortcuts Cheat Sheet"
          >
            <Info className="w-5 h-5" />
          </button>
        </div>
      </div>

      {showShortcutModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl p-6 max-w-sm w-full shadow-2xl border border-slate-100 animate-in fade-in zoom-in-95 duration-150">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-bold text-slate-800 font-serif">Keyboard Shortcuts</h3>
                <p className="text-xs text-slate-400 font-semibold">POS Speed Billing Hotkeys</p>
              </div>
              <button
                onClick={() => setShowShortcutModal(false)}
                className="text-slate-400 hover:text-slate-600 font-bold"
              >
                ✕
              </button>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center text-xs py-1.5 border-b border-slate-50">
                <span className="font-semibold text-slate-600">Focus Search Bar</span>
                <kbd className="px-2 py-1 bg-slate-150 border border-slate-200 rounded text-[10px] font-black font-mono shadow-sm">/  or  ⌥+S</kbd>
              </div>
              <div className="flex justify-between items-center text-xs py-1.5 border-b border-slate-50">
                <span className="font-semibold text-slate-600">Add Searched Item</span>
                <kbd className="px-2 py-1 bg-slate-150 border border-slate-200 rounded text-[10px] font-black font-mono shadow-sm">Enter</kbd>
              </div>
              <div className="flex justify-between items-center text-xs py-1.5 border-b border-slate-50">
                <span className="font-semibold text-slate-600">Generate Bill (Checkout)</span>
                <kbd className="px-2 py-1 bg-slate-150 border border-slate-200 rounded text-[10px] font-black font-mono shadow-sm">⌘+Enter  or  ⌥+G</kbd>
              </div>
              <div className="flex justify-between items-center text-xs py-1.5 border-b border-slate-50">
                <span className="font-semibold text-slate-600">Cancel Active Order</span>
                <kbd className="px-2 py-1 bg-slate-150 border border-slate-200 rounded text-[10px] font-black font-mono shadow-sm">⌥+C</kbd>
              </div>
              <div className="flex justify-between items-center text-xs py-1.5 border-b border-slate-50">
                <span className="font-semibold text-slate-600">Back to Floor Layout</span>
                <kbd className="px-2 py-1 bg-slate-150 border border-slate-200 rounded text-[10px] font-black font-mono shadow-sm">⌥+H</kbd>
              </div>
              <div className="flex justify-between items-center text-xs py-1.5">
                <span className="font-semibold text-slate-600">Close Modals / Unfocus</span>
                <kbd className="px-2 py-1 bg-slate-150 border border-slate-200 rounded text-[10px] font-black font-mono shadow-sm">Esc</kbd>
              </div>
            </div>
            <button
              onClick={() => setShowShortcutModal(false)}
              className="w-full mt-6 bg-slate-800 hover:bg-slate-900 text-white font-bold py-2.5 rounded-xl text-xs transition duration-150"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchBar;
